import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

/**
 * Toast mínimo e sem dependências, com API no formato do `sonner` (usado pelo
 * Miror PRO): `toast.success(msg)`, `toast.error(msg)`, `toast.info(msg)`,
 * `toast(msg)`. Visual ancorado nos tokens do design system (light/dark
 * automáticos). Se um dia o app adotar `sonner`, a troca é drop-in.
 */
type ToastTone = 'success' | 'error' | 'info' | 'default';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  duration: number;
}

type Listener = (items: ToastItem[]) => void;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(items);
}

function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(message: string, tone: ToastTone, duration = 4000) {
  const id = ++seq;
  items = [...items, { id, message, tone, duration }];
  emit();
  return id;
}

export const toast = Object.assign(
  (message: string, opts?: { duration?: number }) => push(message, 'default', opts?.duration),
  {
    success: (message: string, opts?: { duration?: number }) => push(message, 'success', opts?.duration),
    error: (message: string, opts?: { duration?: number }) => push(message, 'error', opts?.duration ?? 5000),
    info: (message: string, opts?: { duration?: number }) => push(message, 'info', opts?.duration),
    dismiss,
  },
);

const TONE_ICON = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  default: Info,
} as const;

const TONE_ACCENT = {
  success: 'text-success-solid',
  error: 'text-destructive-solid',
  info: 'text-info-solid',
  default: 'text-muted-foreground',
} as const;

function ToastRow({ item }: { item: ToastItem }) {
  useEffect(() => {
    const t = setTimeout(() => dismiss(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item.id, item.duration]);

  const Icon = TONE_ICON[item.tone];
  return (
    <div
      role="status"
      className="pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-lg animate-in slide-in-from-top-2 fade-in-0 duration-200"
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${TONE_ACCENT[item.tone]}`} />
      <p className="min-w-0 flex-1 text-sm leading-snug text-foreground">{item.message}</p>
      <button
        type="button"
        aria-label="Fechar"
        onClick={() => dismiss(item.id)}
        className="-mr-1 -mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Monte uma vez no root do app. Renderiza a pilha de toasts (top-right). */
export function Toaster() {
  const [list, setList] = useState<ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col gap-2">
      {list.map((item) => (
        <ToastRow key={item.id} item={item} />
      ))}
    </div>,
    document.body,
  );
}
